import SiteLayout from '@/components/site/SiteLayout';
import { SITE_URL } from '@/lib/seoRoutes';
import {
  Banknote,
  BarChart3,
  CalendarCheck,
  CheckCircle2,
  Clock3,
  Download,
  Smartphone,
  FileUp,
  Gift,
  IdCard,
  ListChecks,
  Play,
  Share2,
  ShieldCheck,
  ChevronUp,
  Star,
  Stethoscope,
  UserPlus,
  Zap,
} from 'lucide-react';
import Image from 'next/image';
import Link from 'next/link';
import { useState } from 'react';

const whyResidentsChoose = [
  {
    title: 'Flexible Scheduling',
    body: 'Pick up shifts that fit your availability-days, evenings, or weekends.',
  },
  {
    title: 'Income Without Burnout',
    body: 'Earn meaningful income without taking on overwhelming extra work.',
  },
  {
    title: 'Real Clinical Experience',
    body: 'Stay involved in imaging workflows and contrast administration in a practical, hands-on way.',
  },
  {
    title: 'Simple, Streamlined Process',
    body: 'We handle the logistics so you can focus on showing up and doing the work.',
  },
];

const doingPoints = [
  'Provide contrast coverage for CT and MRI studies',
  'Be available for patient monitoring and support',
  'Assist with protocol adherence and safety measures',
  'Work alongside experienced imaging center staff',
];

const whoItsFor = [
  'Want to earn extra income during training',
  'Are interested in radiology, cardiology, or imaging workflows',
  'Prefer flexible, shift-based opportunities',
  'Want to gain experience in outpatient settings',
];

const bigQuestions = [
  {
    title: 'Will this interfere with my residency schedule?',
    body: 'No-shifts are flexible and designed to fit around your availability.',
  },
  {
    title: 'Is this compliant with training requirements?',
    body: 'We work with you to ensure roles align with appropriate guidelines and expectations.',
  },
  {
    title: 'Is support provided?',
    body: 'Yes. You will work within established protocols and alongside experienced teams.',
  },
];

const whyThisMatters = [
  'Use your skills productively',
  'Earn on your own schedule',
  'Gain relevant clinical experience',
];

const residentChoiceCards = [
  {
    title: 'Total Schedule Control',
    body: 'Pick up shifts only when you want. Filter by date, duration, and facility type.',
    icon: CalendarCheck,
    tone: 'gold',
  },
  {
    title: 'A-Rated Malpractice',
    body: 'Comprehensive insurance with tail coverage included. Peace of mind comes standard.',
    icon: ShieldCheck,
    tone: 'navy',
  },
  {
    title: 'Streamlined Credentialing',
    body: 'Upload once. We handle paperwork, licensing, and facility onboarding for you.',
    icon: Zap,
    tone: 'navy',
  },
  {
    title: 'Transparent, Fast Pay',
    body: 'See exact rates upfront. Get paid via direct deposit!',
    icon: Banknote,
    tone: 'gold',
  },
];

const walkthroughLessons = [
  {
    title: 'Account Setup',
    body: 'Create your account and verify your email',
    icon: UserPlus,
  },
  {
    title: 'Profile Building',
    body: 'Complete your professional profile details',
    icon: IdCard,
  },
  {
    title: 'Document Upload',
    body: 'Upload licenses and credentials',
    icon: FileUp,
  },
  {
    title: 'Finding Shifts',
    body: 'Browse and book perfect shifts',
    icon: CalendarCheck,
  },
  {
    title: 'Working Shifts',
    body: 'Check-in, track hours, and log encounters',
    icon: Stethoscope,
  },
  {
    title: 'Getting Paid',
    body: 'Submit timesheets and receive payment',
    icon: Banknote,
  },
];

const referralSteps = [
  {
    title: 'Refer a Resident',
    body: 'Share DocMoonlight with fellow residents through our simple referral form or unique link.',
    icon: UserPlus,
  },
  {
    title: 'They Complete First Shift',
    body: 'Your referral signs up, gets credentialed, and successfully completes their first eligible shift.',
    icon: CalendarCheck,
  },
  {
    title: 'You Earn $500',
    body: 'Receive up to $500 referral bonus via a direct deposit.',
    icon: Banknote,
  },
];

const residentFaqs = [
  {
    question: 'Do I need my own malpractice insurance?',
    answer:
      'No. DocMoonlight provides comprehensive, A-rated malpractice insurance with tail coverage for every shift you work through our platform.',
  },
  {
    question: 'How long does credentialing take?',
    answer:
      "Once you upload your basic documents, our team can often get you cleared for your first facility within 2-4 weeks, depending on the specific hospital's requirements.",
  },
  {
    question: 'Are there minimum shift requirements?',
    answer:
      "Absolutely not. We understand residency is demanding. You can work one shift a month or several a week-it is entirely up to you and your program's duty hour limits.",
  },
];

const residentFitCards = [
  {
    title: 'PGY-2 and Above',
    body: 'Residents who have completed their intern year and hold a full unrestricted state medical license.',
  },
  {
    title: 'Research, Gap Year, or Elective Time',
    body: 'Residents or fellows on research blocks, gap years, or elective rotations who have availability to moonlight with program approval.',
  },
  {
    title: 'Program Approved',
    body: 'Trainees who have explicit permission from their Program Director to moonlight.',
  },
];

const residentReviews = [
  {
    title: 'IM Resident',
    author: 'Gadsters',
    version: '4.1',
    source: 'App Store',
    body: "As a 3rd year IM resident, I was having a really difficult time finding moonlighting opportunities. Docmoonlight has been one of the smoothest experiences I've had. Super quick onboarding, easy shift pickup, transparent pay, and fast communication. The app makes finding shifts really efficient and the process feels much more streamlined compared to traditional LOCUM agencies. Highly recommend for residents looking for convenient moonlighting opportunities!",
  },
  {
    title: 'Great gig',
    author: 'ghdyhcdnsa',
    version: '3.5',
    source: 'App Store',
    body: 'Very happy with these moonlighting opportunities and feel that overall the system and the app have worked well. Would recommend',
  },
  {
    title: 'Exciting Side Gig',
    author: 'josephlois2020',
    version: '3.3',
    source: 'App Store',
    body: "Docmoonlight is an exciting side gig for Residents and Fellows, that unlocks your potential to boost your income without compromising your study. They offered me a flexible schedule, where shifts are available on a first-come, first-served basis, perfectly tailored to fit around my residency hours. I've been able to turn my downtime into dollars with this straightforward yet important medical role.",
  },
  {
    title: 'Amazing',
    author: 'Kyaw Zaw Hein',
    version: '3.0',
    source: 'App Store',
    body: 'Amazing',
  },
  {
    title: 'Great Moonlighting Experience',
    author: 'ATA gurl',
    version: '3.0',
    source: 'App Store',
    body: 'This was a great opportunity for me to make extra money while studying for boards. The schedule is very flexible and I get to choose my own shifts each month. This is the ideal low stress moonlighting experience for a resident.',
  },
  {
    title: 'Amazing opportunity',
    author: 'Akumoh',
    version: '3.6',
    source: 'App Store',
    body: 'This is such an amazing gig that provides a boost in income and is relatively low stress, allowing you time to study for boards or catch up work. Highly recommend this for any residents looking for great moonlighting opportunities',
  },
  {
    title: 'Great Opportunity',
    author: 'YupItsTroo',
    version: '3.5',
    source: 'App Store',
    body: 'Works well, as advertised. Functional and efficient to start.',
  },
  {
    title: 'Excellent opportunity for residents',
    author: 'ZN MD',
    version: '3.3',
    source: 'App Store',
    body: 'Easy process to start. Flexible work hours and afternoon hours available. Definitely recommend for any residents looking for moonlighting!',
  },
  {
    title: 'Great Opportunity',
    author: 'AreebaSK',
    version: '3.0',
    source: 'App Store',
    body: 'This is truly a great opportunity for residents to earn extra money while in residency. Your main responsibility is to monitor, assess, and treat patients for contrast dye reactions during imaging procedures. The shifts are on a first come first get basis and are very flexible. It is a great way to earn extra income while learning/studying/ and practicing medicine.',
  },
];

const residentJobSearchTopics = [
  'Resident moonlighting jobs',
  'Moonlighting jobs for medical residents',
  'Physician moonlighting opportunities',
  'Flexible jobs for residents and fellows',
  'Outpatient imaging center shifts',
  'Contrast coverage shifts for physicians',
];

const residentsPageUrl = `${SITE_URL}/residents`;

const residentStructuredData = [
  {
    '@context': 'https://schema.org',
    '@type': 'Service',
    name: 'Resident Moonlighting Jobs',
    serviceType: 'Resident moonlighting jobs and physician shifts',
    provider: {
      '@type': 'Organization',
      name: 'DocMoonlight',
      url: SITE_URL,
    },
    areaServed: {
      '@type': 'Country',
      name: 'United States',
    },
    audience: [
      {
        '@type': 'Audience',
        audienceType: 'Medical residents',
      },
      {
        '@type': 'Audience',
        audienceType: 'Medical fellows',
      },
      {
        '@type': 'Audience',
        audienceType: 'Physicians in training',
      },
    ],
    description:
      'DocMoonlight helps eligible medical residents and fellows find flexible moonlighting jobs, physician shifts, outpatient imaging center coverage opportunities, credentialing support, malpractice coverage, and app-based scheduling.',
    url: residentsPageUrl,
    termsOfService: `${SITE_URL}/PrivacyPolicy`,
  },
  {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: residentFaqs.map((item) => ({
      '@type': 'Question',
      name: item.question,
      acceptedAnswer: {
        '@type': 'Answer',
        text: item.answer,
      },
    })),
  },
  {
    '@context': 'https://schema.org',
    '@type': 'ItemList',
    name: 'Resident job searches DocMoonlight helps with',
    itemListElement: residentJobSearchTopics.map((topic, index) => ({
      '@type': 'ListItem',
      position: index + 1,
      name: topic,
      url: residentsPageUrl,
    })),
  },
];

const ResidentsPage = () => {
  const [openFaqs, setOpenFaqs] = useState(() =>
    residentFaqs.map((item) => item.question),
  );

  const toggleFaq = (question: string) => {
    setOpenFaqs((current) =>
      current.includes(question)
        ? current.filter((item) => item !== question)
        : [...current, question],
    );
  };

  return (
    <SiteLayout
      title='Resident Moonlighting Jobs | Flexible Physician Shifts | DocMoonlight'
      description='Find flexible moonlighting shifts for residents and fellows, including virtual and onsite physician coverage opportunities with transparent earning potential.'
      keywords={[
        'resident moonlighting',
        'moonlighting jobs for residents',
        'fellow moonlighting',
        'physician shifts',
      ]}
      structuredData={residentStructuredData}
    >
      <section className='relative overflow-hidden bg-[#f7f7f7] px-4 py-20 sm:px-6 lg:px-8 lg:py-28'>
        <div className='pointer-events-none absolute left-[8%] bottom-8 h-64 w-64 rounded-full bg-white blur-3xl' />
        <div className='pointer-events-none absolute right-[12%] top-[-6rem] h-72 w-72 rounded-full bg-white blur-3xl' />

        <div className='relative z-10 mx-auto grid max-w-7xl gap-14 lg:grid-cols-[0.9fr_1.1fr] lg:items-center'>
          <div>
            <div className='inline-flex items-center gap-3 rounded-full border border-[#e5e7eb] bg-white px-4 py-2 text-sm font-black text-[#14213d] shadow-sm'>
              <span className='h-2 w-2 rounded-full bg-[#f7c467]' />
              For Medical Residents &amp; Fellows
            </div>

            <h1 className='mt-10 max-w-2xl text-[2.55rem] font-black leading-[1.02] tracking-tight text-black md:text-[5.1rem] md:leading-[0.96]'>
              Flexible moonlighting,
              <br className='hidden md:block' />
              <span className='relative inline-block text-[#14213d]'>
                on your terms.
                <span className='absolute -bottom-2 left-[18%] h-1.5 w-[70%] rounded-full bg-[#f7c467]' />
              </span>
            </h1>

            <p className='mt-8 max-w-2xl text-[1.35rem] font-semibold leading-8 text-[#34495e]'>
              Discover stress-free, high-paying shifts that fit perfectly around
              your training schedule. We handle the credentialing, you focus on
              care.
            </p>

            <div className='mt-12 flex flex-col gap-4 sm:flex-row'>
              <Link
                href='/contact#contact-form'
                className='inline-flex min-h-14 items-center justify-center rounded-full bg-black px-8 text-base font-black text-white shadow-[0_16px_35px_rgba(0,0,0,0.18)] transition hover:bg-[#14213d]'
              >
                Have Questions?
              </Link>
              <Link
                href='#download-app'
                className='inline-flex min-h-14 items-center justify-center gap-2 rounded-full border border-[#d9dee7] bg-white px-8 text-base font-black text-black shadow-sm transition hover:border-[#fca311]'
              >
                <Download className='h-5 w-5' />
                Download App
              </Link>
            </div>

            <div className='mt-10 flex flex-wrap gap-x-3 gap-y-3 text-[0.68rem] font-black text-[#34495e] sm:mt-14 sm:gap-x-9 sm:gap-y-4 sm:text-sm'>
              {[
                'Flexible Shifts',
                'Malpractice Covered',
                'Fast Onboarding',
              ].map((item) => (
                <div
                  key={item}
                  className='inline-flex items-center gap-1.5 sm:gap-2'
                >
                  <CheckCircle2 className='h-3.5 w-3.5 fill-[#fca311] text-[#fca311] sm:h-4 sm:w-4' />
                  {item}
                </div>
              ))}
            </div>
          </div>

          <div className='relative mx-auto w-full max-w-3xl overflow-hidden rounded-[1.65rem] shadow-[0_30px_85px_rgba(20,33,61,0.18)]'>
            <iframe
              className='aspect-video w-full'
              src='https://www.youtube.com/embed/ndLBUynhnXo'
              title='Resident Overview'
              loading='lazy'
              allow='accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share'
              allowFullScreen
            />
          </div>
        </div>
      </section>

      <section className='bg-white px-4 py-20 sm:px-6 lg:px-8 lg:py-28'>
        <div className='mx-auto max-w-7xl'>
          <div className='text-center'>
            <h2 className='text-[2rem] font-black leading-[1.05] tracking-tight text-black md:text-[4rem] md:leading-[1.02]'>
              Sign Up in Under 10 Minutes!
            </h2>
            <p className='mt-5 text-xl font-semibold text-[#14213d]'>
              Experience the hassle-free way to moonlight and boost your income
              today!
            </p>
          </div>

          <div className='relative mx-auto mt-12 w-full max-w-5xl overflow-hidden rounded-[1.7rem] shadow-[0_30px_90px_rgba(15,23,42,0.18)]'>
            <iframe
              className='aspect-video w-full'
              src='https://www.youtube.com/embed/5JmJuJBs7e8'
              title='How to Get Started'
              loading='lazy'
              allow='accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share'
              allowFullScreen
            />
          </div>

          <div className='mt-16 grid gap-6 md:grid-cols-2 lg:grid-cols-4'>
            {residentChoiceCards.map((card) => {
              const Icon = card.icon;
              const isGold = card.tone === 'gold';

              return (
                <article
                  key={card.title}
                  className='min-h-[16rem] rounded-xl border border-[#edf0f4] bg-white p-7 shadow-[0_14px_45px_rgba(15,23,42,0.06)]'
                >
                  <span
                    className={`inline-flex h-12 w-12 items-center justify-center rounded-xl ${
                      isGold
                        ? 'bg-[#fff2dc] text-[#fca311]'
                        : 'bg-[#f3f4f6] text-[#14213d]'
                    }`}
                  >
                    <Icon className='h-6 w-6' />
                  </span>
                  <h3 className='mt-8 whitespace-nowrap text-[1.02rem] font-black leading-tight text-black lg:text-[1rem]'>
                    {card.title}
                  </h3>
                  <p className='mt-4 text-base font-semibold leading-7 text-[#14213d]'>
                    {card.body}
                  </p>
                </article>
              );
            })}
          </div>
        </div>
      </section>

      <section className='bg-[#f7f7f7] px-4 py-20 sm:px-6 lg:px-8 lg:py-28'>
        <div className='mx-auto max-w-7xl'>
          <div className='text-center'>
            <h2 className='text-[2rem] font-black leading-tight tracking-tight text-black md:text-[3.6rem]'>
              Complete App Walkthrough
            </h2>
          </div>

          <article className='mx-auto mt-20 max-w-5xl overflow-hidden rounded-xl bg-white shadow-[0_22px_65px_rgba(15,23,42,0.12)]'>
            <div
              id='resident-tutorial-video'
              className='relative overflow-hidden bg-black'
            >
              <iframe
                className='aspect-video w-full'
                src='https://www.youtube.com/embed/gis13jqJQRM'
                title='Complete app walkthrough tutorial'
                loading='lazy'
                allow='accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share'
                allowFullScreen
              />
            </div>

            <div className='p-8 md:p-10'>
              <h3 className='text-2xl font-black tracking-tight text-black md:text-3xl'>
                Full Platform Walkthrough
              </h3>
              <p className='mt-5 text-base font-semibold leading-7 text-[#14213d] md:text-lg'>
                This comprehensive tutorial covers everything you need to know:
                account creation, profile setup, credential uploads, browsing
                and booking shifts, working your first shift, and receiving
                payment. Get started with DocMoonlight in just 5:57 min.
              </p>

              <div className='mt-7 flex flex-wrap gap-x-3 gap-y-3 text-[0.68rem] font-semibold text-[#34495e] sm:gap-x-8 sm:text-sm md:text-base'>
                <span className='inline-flex items-center gap-1.5 sm:gap-2'>
                  <Clock3 className='h-3.5 w-3.5 fill-[#fca311] text-[#fca311] sm:h-4 sm:w-4' />
                  5:57 min
                </span>
                <span className='inline-flex items-center gap-1.5 sm:gap-2'>
                  <BarChart3 className='h-3.5 w-3.5 text-[#fca311] sm:h-4 sm:w-4' />
                  Beginner friendly
                </span>
                <span className='inline-flex items-center gap-1.5 sm:gap-2'>
                  <ListChecks className='h-3.5 w-3.5 text-[#fca311] sm:h-4 sm:w-4' />
                  Step-by-step guide
                </span>
              </div>

              <div className='mt-8 grid gap-4 md:grid-cols-2'>
                <a
                  href='#download-app'
                  className='inline-flex min-h-14 items-center justify-center gap-3 rounded-full bg-[#fca311] px-8 text-base font-black text-black transition hover:bg-[#ff8a00]'
                >
                  <Download className='h-5 w-5' />
                  Download the App
                </a>
                <Link
                  href='/contact#contact-form'
                  className='inline-flex min-h-14 items-center justify-center gap-3 rounded-full border border-[#d9dee7] bg-[#f3f4f6] px-8 text-base font-black text-black transition hover:border-[#fca311]'
                >
                  <Download className='h-5 w-5' />
                  Ask a Question
                </Link>
              </div>
            </div>
          </article>
        </div>
      </section>

      <section className='overflow-hidden bg-white px-0 py-20 sm:px-6 lg:px-8 lg:py-28'>
        <div className='mx-auto max-w-7xl'>
          <div className='px-4 sm:px-0 md:flex md:items-end md:justify-between md:gap-10'>
            <div>
              <p className='text-sm font-black uppercase tracking-[0.22em] text-[#f59e0b]'>
                RESIDENT & FELLOW REVIEWS
              </p>
              <h2 className='mt-4 text-[2rem] font-black leading-tight tracking-tight text-black md:text-[3.6rem]'>
                Trusted by Residents & Fellows
              </h2>
              <p className='mt-4 max-w-2xl text-base font-semibold leading-7 text-[#14213d] md:text-lg'>
                See how physicians in training use DocMoonlight to find flexible
                moonlighting opportunities, manage shifts, and earn additional
                income through one simple app.
              </p>
            </div>
          </div>

          <div className='resident-review-marquee-shell marquee-shell mt-12'>
            <div className='marquee-track resident-review-marquee-track'>
              {[...residentReviews, ...residentReviews].map((review, index) => (
                <article
                  key={`${review.title}-${review.author}-${index}`}
                  className='resident-review-card'
                >
                  <div className='flex items-center gap-4'>
                    <div
                      className='flex text-[#fbbf24]'
                      aria-label='5 star rating'
                    >
                      {Array.from({ length: 5 }).map((_, starIndex) => (
                        <Star
                          key={starIndex}
                          className='h-4 w-4 fill-current'
                        />
                      ))}
                    </div>
                  </div>

                  <h3 className='mt-5 line-clamp-2 min-h-[2.75rem] text-lg font-black leading-tight text-[#111827]'>
                    {review.title}
                  </h3>
                  <p className='mt-4 line-clamp-6 min-h-[10.5rem] text-sm font-semibold leading-7 text-slate-600'>
                    &quot;{review.body}&quot;
                  </p>

                  <div className='mt-6 border-t border-slate-100 pt-5'>
                    <div className='flex items-center gap-3'>
                      <span className='inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-[#fff2dc] text-sm font-black text-[#f59e0b]'>
                        {review.author
                          .split(' ')
                          .map((name) => name[0])
                          .join('')
                          .slice(0, 2)}
                      </span>
                      <div>
                        <div className='text-sm font-black text-[#14213d]'>
                          {review.author}
                        </div>
                        <div className='mt-0.5 text-[0.68rem] font-bold text-slate-400'>
                          {review.source} - v{review.version}
                        </div>
                      </div>
                    </div>
                  </div>
                </article>
              ))}
            </div>
          </div>

          <div className='mx-auto mt-8 flex max-w-3xl items-center justify-center gap-4 border-t border-slate-200 pt-5 sm:mt-10 sm:gap-10 sm:pt-7'>
            <div className='flex items-center gap-2 sm:gap-3'>
              <span className='inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-[#fff2dc] text-[#f59e0b] sm:h-11 sm:w-11'>
                <Smartphone className='h-4 w-4 sm:h-5 sm:w-5' />
              </span>
              <div className='text-left'>
                <div className='text-xl font-black leading-none text-[#14213d] sm:text-2xl'>
                  25K+
                </div>
                <div className='mt-1 text-[0.58rem] font-black uppercase tracking-[0.1em] text-slate-500 sm:text-xs sm:tracking-[0.18em]'>
                  Total Downloads
                </div>
              </div>
            </div>

            <div className='h-9 w-px shrink-0 bg-slate-200 sm:h-10' />

            <div className='flex items-center gap-2 sm:gap-3'>
              <span className='inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-[#fff2dc] text-[#f59e0b] sm:h-11 sm:w-11'>
                <Star className='h-4 w-4 fill-current sm:h-5 sm:w-5' />
              </span>
              <div className='text-left'>
                <div className='text-xl font-black leading-none text-[#14213d] sm:text-2xl'>
                  4.9
                </div>
                <div className='mt-1 text-[0.58rem] font-black uppercase tracking-[0.1em] text-slate-500 sm:text-xs sm:tracking-[0.18em]'>
                  Average Rating
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className='bg-[#f7f7f7] px-4 py-10 sm:px-6 lg:px-8'>
        <div className='relative mx-auto max-w-7xl overflow-hidden rounded-[1.55rem] bg-[linear-gradient(135deg,#14213d_0%,#05070c_100%)] px-6 py-16 text-white shadow-[0_24px_75px_rgba(15,23,42,0.14)] md:px-10'>
          <div className='absolute inset-0 opacity-18 [background-image:radial-gradient(circle,rgba(255,255,255,0.62)_1px,transparent_1px)] [background-size:32px_32px]' />
          <div className='relative z-10 mx-auto max-w-6xl'>
            <div className='text-center'>
              <h2 className='text-[1.85rem] font-black leading-tight tracking-tight text-white md:text-[3rem]'>
                What You&apos;ll Learn
              </h2>
              <p className='mt-5 text-lg font-black text-white md:text-xl'>
                Everything covered in this comprehensive 4-minute tutorial
              </p>
            </div>

            <div className='mt-12 grid gap-6 md:grid-cols-2 lg:grid-cols-3'>
              {walkthroughLessons.map((lesson) => {
                const Icon = lesson.icon;

                return (
                  <article
                    key={lesson.title}
                    className='min-h-[11.75rem] rounded-xl border border-white/12 bg-white/7 p-7 shadow-[inset_0_-55px_75px_rgba(255,255,255,0.02)]'
                  >
                    <span className='inline-flex h-12 w-12 items-center justify-center rounded-lg bg-[#5b4930] text-[#fca311]'>
                      <Icon className='h-6 w-6' />
                    </span>
                    <h3 className='mt-6 text-xl font-black text-white'>
                      {lesson.title}
                    </h3>
                    <p className='mt-3 text-base font-black leading-6 text-white/55'>
                      {lesson.body}
                    </p>
                  </article>
                );
              })}
            </div>
          </div>
        </div>
      </section>

      <section className='bg-white px-4 py-20 sm:px-6 lg:px-8 lg:py-28'>
        <div className='mx-auto grid max-w-7xl gap-14 lg:grid-cols-[0.95fr_1fr] lg:items-center'>
          <div className='relative mx-auto h-[35rem] w-full max-w-xl overflow-hidden rounded-[1.55rem] shadow-[0_28px_80px_rgba(15,23,42,0.14)]'>
            <Image
              src='https://images.unsplash.com/photo-1622253692010-333f2da6031d?auto=format&fit=crop&w=1200&q=82'
              alt='Resident physician using a phone in a hospital hallway'
              fill
              sizes='(max-width: 1023px) 100vw, 50vw'
              className='object-cover'
            />
            <div className='absolute inset-0 bg-gradient-to-t from-black/72 via-black/10 to-transparent' />
            <div className='absolute bottom-9 left-8 right-8 text-white'>
              <blockquote className='text-2xl font-black leading-tight md:text-[1.65rem]'>
                &quot;DocMoonlight gave me a flexible way to earn extra income
                without adding more stress to residency.&quot;
              </blockquote>
              <div className='mt-6 text-lg font-black'>
                - Dr. Jamal Williams, PGY-3
              </div>
            </div>
          </div>

          <div>
            <h2 className='text-[2.1rem] font-black leading-tight tracking-tight text-black md:text-[4rem]'>
              Who This Is For
            </h2>

            <div className='mt-12 space-y-6'>
              {residentFitCards.map((item) => (
                <article
                  key={item.title}
                  className='rounded-xl bg-[#e5e5e5] p-7'
                >
                  <div className='flex gap-5'>
                    <CheckCircle2 className='mt-1 h-6 w-6 shrink-0 fill-[#fca311] text-[#fca311]' />
                    <div>
                      <h3 className='text-xl font-black text-black'>
                        {item.title}
                      </h3>
                      <p className='mt-3 text-base font-semibold leading-7 text-[#14213d]'>
                        {item.body}
                      </p>
                    </div>
                  </div>
                </article>
              ))}
            </div>
          </div>
        </div>
      </section>

      <section className='bg-[#fbf8f1] px-4 py-20 sm:px-6 lg:px-8 lg:py-28'>
        <div className='mx-auto max-w-7xl'>
          <div className='text-center'>
            <div className='inline-flex items-center gap-2 rounded-full border border-[#fca311]/25 bg-[#fff4df] px-4 py-2 text-sm font-black text-[#f28c00]'>
              <Gift className='h-4 w-4' />
              Refer &amp; Earn
            </div>

            <h2 className='mx-auto mt-8 max-w-5xl text-[2.1rem] font-black leading-[1.05] tracking-tight text-black md:text-[4.25rem] md:leading-[1.02]'>
              Help other residents find
              <br />
              flexible ways to earn-and
              <br />
              get rewarded.
            </h2>

            <p className='mx-auto mt-8 max-w-4xl text-xl font-semibold leading-9 text-[#14213d]'>
              Refer a medical resident to DocMoonlight, and when they complete
              their first eligible shift, you&apos;ll receive up to{' '}
              <span className='font-black text-[#fca311]'>$500</span> for every
              successful referral on eligible locations.
            </p>

            <p className='mx-auto mt-8 max-w-4xl text-lg font-semibold leading-8 text-[#34495e]'>
              No complicated tracking. No hoops. Just share the opportunity and
              get paid when they get started.
            </p>
          </div>

          <h3 className='mt-20 text-center text-[2rem] font-black tracking-tight text-black'>
            How It Works
          </h3>

          <div className='mx-auto mt-12 grid max-w-5xl gap-8 md:grid-cols-3'>
            {referralSteps.map((step, index) => {
              const Icon = step.icon;
              const isMiddle = index === 1;

              return (
                <article
                  key={step.title}
                  className='relative min-h-[19rem] rounded-xl border border-[#e7ebf0] bg-white p-8 pt-12 shadow-[0_14px_34px_rgba(15,23,42,0.1)]'
                >
                  <span className='absolute -top-7 left-8 inline-flex h-14 w-14 items-center justify-center rounded-full bg-[#fca311] text-xl font-black text-white shadow-[0_12px_26px_rgba(252,163,17,0.2)]'>
                    {index + 1}
                  </span>
                  <span
                    className={`inline-flex h-16 w-16 items-center justify-center rounded-xl ${
                      isMiddle
                        ? 'bg-[#eef0f4] text-[#14213d]'
                        : 'bg-[#fff2dc] text-[#fca311]'
                    }`}
                  >
                    <Icon className='h-8 w-8' />
                  </span>
                  <h4 className='mt-8 text-xl font-black leading-tight text-black'>
                    {step.title}
                  </h4>
                  <p className='mt-5 text-base font-semibold leading-7 text-[#14213d]'>
                    {step.body}
                  </p>
                </article>
              );
            })}
          </div>

          <div className='relative mt-20 overflow-hidden rounded-[1.5rem] border border-slate-200 bg-[radial-gradient(circle_at_top_right,_rgba(252,163,17,0.13),_transparent_26%),linear-gradient(180deg,#ffffff_0%,#f8fbff_100%)] px-6 py-14 text-center shadow-[0_24px_70px_rgba(15,23,42,0.1)] md:px-10'>
            <div className='absolute inset-0 opacity-60 [background-image:radial-gradient(circle,rgba(20,33,61,0.08)_1px,transparent_1px)] [background-size:32px_32px]' />
            <div className='relative z-10'>
              <h3 className='text-[1.75rem] font-black leading-tight tracking-tight text-slate-950 md:text-[3rem]'>
                Start Referring Today
              </h3>
              <p className='mx-auto mt-6 max-w-3xl text-lg font-black leading-8 text-[#14213d] md:text-xl'>
                Share the opportunity with your fellow residents and start
                earning rewards.
              </p>
              <div className='mt-10 flex flex-col justify-center gap-4 sm:flex-row'>
                <Link
                  href='/contact#contact-form'
                  className='inline-flex min-h-14 items-center justify-center gap-3 rounded-full bg-[linear-gradient(90deg,#ffb629_0%,#f59e0b_100%)] px-10 text-base font-black text-[#111827] shadow-[0_18px_34px_rgba(245,158,11,0.22)] transition hover:translate-y-[-1px] md:min-h-16 md:text-lg'
                >
                  <Share2 className='h-5 w-5' />
                  Start Your Referral
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className='bg-[#eeeeec] px-4 py-20 sm:px-6 lg:px-8 lg:py-28'>
        <div className='mx-auto max-w-5xl'>
          <div className='text-center'>
            <h2 className='text-[2rem] font-black leading-tight tracking-tight text-black md:text-[4rem]'>
              Addressing the Big Questions
            </h2>
            <p className='mt-8 text-xl font-semibold text-[#14213d]'>
              Everything you need to know about moonlighting with us.
            </p>
          </div>

          <div className='mx-auto mt-20 max-w-4xl space-y-6'>
            {residentFaqs.map((item) => (
              <article
                key={item.question}
                className='rounded-xl border border-[#dfe3e8] bg-white p-8 shadow-[0_8px_18px_rgba(15,23,42,0.06)]'
              >
                <button
                  type='button'
                  onClick={() => toggleFaq(item.question)}
                  className='flex w-full items-start justify-between gap-6 text-left'
                  aria-expanded={openFaqs.includes(item.question)}
                >
                  <span className='text-xl font-black leading-tight text-black'>
                    {item.question}
                  </span>
                  <ChevronUp
                    className={`mt-1 h-5 w-5 shrink-0 text-[#fca311] transition ${
                      openFaqs.includes(item.question) ? '' : 'rotate-180'
                    }`}
                  />
                </button>
                {openFaqs.includes(item.question) && (
                  <p className='mt-8 text-lg font-normal leading-8 text-[#14213d]'>
                    {item.answer}
                  </p>
                )}
              </article>
            ))}
          </div>
        </div>
      </section>

      <section
        id='download-app'
        className='px-4 py-20 sm:px-6 lg:px-8 lg:py-28'
      >
        <div className='mx-auto max-w-5xl rounded-[1.75rem] border border-[rgba(13,33,55,0.1)] bg-[linear-gradient(180deg,var(--theme-paper-warm)_0%,var(--theme-surface)_100%)] p-6 shadow-[0_24px_70px_rgba(15,23,42,0.08)] md:p-10'>
          <p className='text-sm font-black uppercase tracking-[0.22em] text-[var(--theme-accent)]'>
            Join DocMoonlight
          </p>
          <h2 className='mt-3 text-2xl font-black tracking-tight text-[var(--theme-dark)] md:text-3xl'>
            Download the app
          </h2>
          <p className='mt-3 max-w-2xl text-base font-semibold leading-7 text-[var(--theme-dark-soft)]'>
            Get the DocMoonlight app on iOS or Android and explore moonlighting
            opportunities directly from your phone.
          </p>
          <div className='mt-5 flex flex-wrap items-center gap-3'>
            <a
              target='_blank'
              href='https://play.google.com/store/apps/details?id=com.docmoonlight'
              rel='noreferrer'
              className='transition hover:opacity-95'
            >
              <Image
                src='/images/google_play_btn.svg'
                alt='Download on Google Play'
                width={170}
                height={55}
                className='h-[55px] w-[170px] rounded-[10px] bg-white'
              />
            </a>
            <a
              target='_blank'
              href='https://apps.apple.com/us/app/docmoonlight/id6448665833'
              rel='noreferrer'
              className='transition hover:opacity-95'
            >
              <Image
                src='/images/app_store_btn.svg'
                alt='Download on the App Store'
                width={170}
                height={55}
                className='h-[55px] w-[170px] rounded-[10px] bg-white'
              />
            </a>
          </div>
        </div>
      </section>
    </SiteLayout>
  );
};

export default ResidentsPage;
